/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Configuración: una puerta en el nav con dos grupos de filas —
 * «Tu negocio» (alcance organización) y «Este local» (alcance sucursal).
 * Las filas navegan a las pantallas existentes del ShellStack sin tocarlas;
 * cada fila hereda el gate de su ítem del manifiesto (módulo, flag, capability,
 * plan) — si no es visible, la fila no aparece.
 */
import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { ModalAwareScrollView } from '../../../shared/modals/ModalAwareScrollView';
import { ShellContentFrame } from '../../../shared/layout/ShellContentFrame';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { TicketModuleHeader } from '../../../shared/ticket/TicketModuleHeader';
import { SettingsNavListCard, SettingsSectionHeader } from '../../../settings/screens/components/settingsScreenLayout';
import { SettingsNavigationRow } from '../../../settings/components/SettingsNavigationRow';
import { NAV_ITEM_DESCRIPTIONS } from '../navItemDescriptions';
import {
  findManifestItemByRoute,
  getManifestItemsForSurface,
} from '../navigationManifest';
import { navigateInShell } from '../rootNavigation';
import { useNavVisibilityContext } from '../useNavVisibilityContext';
import type { NavItemId } from '../navItemId';

type SettingsRowDef = {
  itemId: NavItemId;
  route: string;
  testID: string;
};

const ORG_ROWS: SettingsRowDef[] = [
  { itemId: 'organization', route: 'Organization', testID: 'settings-hub.row.organization' },
  { itemId: 'billing', route: 'Billing', testID: 'settings-hub.row.billing' },
];

const BRANCH_ROWS: SettingsRowDef[] = [
  { itemId: 'branchIdentity', route: 'BranchIdentity', testID: 'settings-hub.row.branch-identity' },
  { itemId: 'branchPosOps', route: 'BranchPosOps', testID: 'settings-hub.row.branch-pos-ops' },
  { itemId: 'branchDevices', route: 'BranchDevices', testID: 'settings-hub.row.branch-devices' },
  { itemId: 'integrationsHub', route: 'IntegrationsHub', testID: 'settings-hub.row.integrations' },
];

export function SettingsHubScreen() {
  const navigation = useNavigation<any>();
  const navCtx = useNavVisibilityContext();

  const visibleRows = useMemo(() => {
    const stackItems = getManifestItemsForSurface('stackOnly', navCtx);
    const visibleRoutes = new Set(stackItems.map((item) => item.route));
    const filter = (rows: SettingsRowDef[]) =>
      rows.flatMap((row) => {
        if (!visibleRoutes.has(row.route)) return [];
        const item = findManifestItemByRoute(row.route);
        return item ? [{ row, item }] : [];
      });
    return {
      org: filter(ORG_ROWS),
      branch: filter(BRANCH_ROWS),
    };
  }, [navCtx]);

  const open = (route: string) => {
    navigateInShell(navigation, route);
  };

  const renderRows = (
    rows: Array<{
      row: SettingsRowDef;
      item: NonNullable<ReturnType<typeof findManifestItemByRoute>>;
    }>,
  ) =>
    rows.map(({ row, item }, index) => (
      <SettingsNavigationRow
        key={row.route}
        title={item.label}
        description={NAV_ITEM_DESCRIPTIONS[row.itemId] ?? ''}
        iconName={item.icon}
        onPress={() => open(row.route)}
        showSeparator={index < rows.length - 1}
        testID={row.testID}
      />
    ));

  return (
    <ShellContentFrame style={styles.shell}>
      <ModalAwareScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <TicketModuleHeader
          kicker="Negocio"
          title="Configuración"
          meta="Tu negocio y este local."
        />
        {visibleRows.org.length > 0 ? (
          <>
            <SettingsSectionHeader title="Tu negocio" />
            <SettingsNavListCard>{renderRows(visibleRows.org)}</SettingsNavListCard>
          </>
        ) : null}
        {visibleRows.branch.length > 0 ? (
          <>
            <SettingsSectionHeader title="Este local" />
            <SettingsNavListCard>{renderRows(visibleRows.branch)}</SettingsNavListCard>
          </>
        ) : null}
      </ModalAwareScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  scroll: { flex: 1 },
  content: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
    gap: spacing.sm,
  },
});
